import { normalizeQuantityUnit } from '../../components/quantityUnit';
import type { VendorDeviceMeta } from '../../components/vendorDevice';
import type { ResourceType } from '../../platform/pipeline-kernel/types';
import type { ResourceQuantityPurpose } from '../../utils/postPaidPricing';
import { hasPricedVendorSelection, isPostPaidPaymentTiming } from '../../utils/postPaidPricing';
import { stringifySubResourceQuantities } from '../../utils/subResourcePricing';
import {
  MS_PROVIDER_TOKEN_RESOURCE_CODE,
  MS_PROVIDER_TOKEN_UNIT,
  MS_SUB_RESOURCE_INPUT_TOKEN_CODE,
  MS_SUB_RESOURCE_OUTPUT_TOKEN_CODE,
} from './constants';

export type MsTokenQuotaLimit = { enabled?: boolean; value?: number } | undefined;

function readPositiveQuota(limit?: MsTokenQuotaLimit): number | undefined {
  if (!limit?.enabled) return undefined;
  const value = Number(limit.value);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/** 从表单 inputTokens / outputTokens 组装 subResourceQuantities JSON */
export function buildMsSubResourceQuantities(
  inputTokens?: MsTokenQuotaLimit,
  outputTokens?: MsTokenQuotaLimit,
  options?: { emptyQuotaDefault?: number },
): string | undefined {
  const input = readPositiveQuota(inputTokens);
  const output = readPositiveQuota(outputTokens);

  if (options?.emptyQuotaDefault != null) {
    return stringifySubResourceQuantities({
      [MS_SUB_RESOURCE_INPUT_TOKEN_CODE]: input ?? options.emptyQuotaDefault,
      [MS_SUB_RESOURCE_OUTPUT_TOKEN_CODE]: output ?? options.emptyQuotaDefault,
    });
  }

  return stringifySubResourceQuantities({
    ...(input != null ? { [MS_SUB_RESOURCE_INPUT_TOKEN_CODE]: input } : {}),
    ...(output != null ? { [MS_SUB_RESOURCE_OUTPUT_TOKEN_CODE]: output } : {}),
  });
}

/** 父资源 requirement：总量模式用总量；子资源（输入/输出）模式固定为 0 */
export function resolveMsTokenRequirement(
  inputTokens?: MsTokenQuotaLimit,
  outputTokens?: MsTokenQuotaLimit,
  billingTotalTokens?: MsTokenQuotaLimit,
  useSubResourcePricing?: boolean,
): string | undefined {
  if (useSubResourcePricing !== true && billingTotalTokens?.enabled && Number(billingTotalTokens.value) > 0) {
    return String(billingTotalTokens.value);
  }
  const input = readPositiveQuota(inputTokens) ?? 0;
  const output = readPositiveQuota(outputTokens) ?? 0;
  // 子资源计价：数量写在 subResourceQuantities，父级 requirement 只能为 0
  if (input > 0 || output > 0) return '0';
  if (useSubResourcePricing === true) return '0';
  return undefined;
}

/** 组装模型订阅 Token 询价 / 下单资源（含子资源数量与厂商设备维度） */
export function buildMsTokenPricingResource(params: {
  /** 所选计费项是否子资源计价；为 true 时走输入/输出子资源，与算价一致 */
  useSubResourcePricing?: boolean;
  billingTotalTokens?: MsTokenQuotaLimit;
  inputTokens?: MsTokenQuotaLimit;
  outputTokens?: MsTokenQuotaLimit;
  strategyName?: string;
  strategyDisplayName?: string;
  providerPricing?: VendorDeviceMeta | null;
  paymentTiming?: string;
  /** @deprecated 先付费固定/自定义已统一：有数量才组装，保留兼容旧调用 */
  skuType?: string;
  /** pricing：后付费询单价用 1；先付费有数量才组装。order：后付费用填写值或 0；先付费有数量才组装 */
  purpose?: ResourceQuantityPurpose;
}): ResourceType | null {
  const {
    useSubResourcePricing,
    billingTotalTokens,
    inputTokens,
    outputTokens,
    strategyName,
    strategyDisplayName,
    providerPricing,
    paymentTiming,
    purpose = 'pricing',
  } = params;
  const postPaid = isPostPaidPaymentTiming(paymentTiming);
  const hasVendor = hasPricedVendorSelection(providerPricing);
  const forOrder = purpose === 'order';

  const useBillingTotal = useSubResourcePricing !== true && Boolean(billingTotalTokens?.enabled && Number(billingTotalTokens.value) > 0);
  const hasSubResourceQuotas = readPositiveQuota(inputTokens) != null || readPositiveQuota(outputTokens) != null;
  // 显式 false：总资源；显式 true：子资源；未标记时按配额推断
  const isSubResourceMode = useSubResourcePricing === true || (useSubResourcePricing !== false && !useBillingTotal && hasSubResourceQuotas);

  let requirement: string | undefined;
  let subResourceQuantities: string | undefined;

  if (isSubResourceMode) {
    subResourceQuantities = buildMsSubResourceQuantities(inputTokens, outputTokens, {
      // 算价后付费空配额补 1；下单组装空配额补 0
      ...(postPaid ? { emptyQuotaDefault: forOrder ? 0 : 1 } : {}),
    });
    // 子资源计价：父级 requirement 固定为 0；先付费无子配额则不组装
    if (postPaid || subResourceQuantities) {
      requirement = '0';
    }
  } else if (postPaid && hasVendor) {
    if (forOrder) {
      // 下单：有总量用总量，否则 0
      requirement = useBillingTotal ? String(billingTotalTokens!.value) : '0';
    } else {
      // 算价：后付费单价固定 requirement=1
      requirement = '1';
    }
  } else if (useBillingTotal) {
    requirement = String(billingTotalTokens!.value);
  } else {
    requirement = undefined;
  }

  if (!requirement) return null;

  // vendor/device 名称与 vendorID/deviceID 均提交；禁止用展示名/ID 互相回填
  const vendor = String(providerPricing?.vendor || strategyDisplayName || '').trim();
  // strategyName 已是 deviceID 语义，不可再回填到 device 名称
  const device = String(providerPricing?.device || '').trim();
  const vendorID = String(providerPricing?.vendorID || '').trim();
  const deviceID = String(providerPricing?.deviceID || strategyName || '').trim();

  return {
    code: MS_PROVIDER_TOKEN_RESOURCE_CODE,
    description: MS_PROVIDER_TOKEN_RESOURCE_CODE,
    unit: normalizeQuantityUnit(providerPricing?.quantityUnit) || MS_PROVIDER_TOKEN_UNIT,
    requirement,
    ...(subResourceQuantities ? { subResourceQuantities } : {}),
    ...(vendor ? { vendor } : {}),
    ...(vendorID ? { vendorID } : {}),
    ...(device ? { device } : {}),
    ...(deviceID ? { deviceID } : {}),
  };
}
